import cx from 'classnames';
import { FormGroup } from 'components/SettingsSidebar/helpers/FormHelpers';
import { approvalApi, useAddApprovalItemMutation } from 'features/comments/approvalApi';
import { documentApi } from 'features/documents/documents';
import { FieldArray, Form as FForm, Formik, useFormikContext } from 'formik';
import { useGetOrganisationUsers } from 'hooks/useGetOrganisationUsers';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useGetApprovalTeams } from 'pages/approvals/hooks/useGetApprovalTeams';
import { useGetArea } from 'pages/documents_v2/hooks/useGetArea';
import { useGetDocument } from 'pages/documents_v2/hooks/useGetDocument';
import { useGetDocumentUserRole } from 'pages/documents_v2/hooks/useGetDocumentUserRole';
import { useGetDocumentUsers } from 'pages/documents_v2/hooks/useGetDocumentUsers';
import { useGetSections } from 'pages/documents_v2/hooks/useGetSections';
import { useGetAreaId } from 'pages/documents_v2/views/edit_area/hooks/useGetAreaId';
import DocumentHelper from 'pages/global/DocumentHelper';
import { FormModal, ModalFooter } from 'pages/global/FormModal';
import HelperFunctions from 'pages/global/HelperFunctions';
import { DocRevSelect, InputField, InputSelect, TextAreaField } from 'pages/publications_v2/helpers/FieldHelper';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Clipboard2Check, InfoCircle, Trash3 } from 'react-bootstrap-icons';
import { useDispatch } from 'react-redux';
import * as Yup from 'yup';

/**
 * @param {import('models/documents.models').Block} block
 * @param {() => void} handleClose
 */
export function CreateTaskModal({ block, handleClose }) {
    const dispatch = useDispatch();
    const areaId = useGetAreaId();
    const area = useGetArea(areaId);
    const [addApprovalItem] = useAddApprovalItemMutation();
    const document = useGetDocument();
    const sections = useGetSections();
    const documentUsers = useGetDocumentUsers();

    const [getAreaBlocks] = documentApi.useLazyGetAreaBlocksQuery();
    const [getDocumentVariants] = documentApi.useLazyGetDocumentVariantsQuery();

    const [initialValues, setInitialValues] = useLocalStorage('edit_area_create_task_modal_initial_values', {
        description: '',
        purpose: 'approve',
        deadline: '',
        reminderDate: '',
        userIds: [],
    });

    const section = HelperFunctions.findInDocument({ type: 'section', id: area.sectionId }, sections);

    return (
        <FormModal
            show={true}
            onHide={handleClose}
            title={
                <div className="flex-center">
                    <Clipboard2Check className="mr-1" />
                    Taak aanmaken
                </div>
            }
            size="lg"
        >
            <Formik
                initialValues={initialValues}
                validationSchema={TaskSchema}
                onSubmit={handleSubmit}
                enableReinitialize={false}
            >
                {({ isSubmitting, isValid, values }) => (
                    <>
                        <FForm autoComplete="off" className="modal-body" id="create-task">
                            <FormLocalStorage formValues={JSON.stringify(values)} setInitialValues={setInitialValues} />
                            <ModalBody />
                        </FForm>

                        <ModalFooter
                            btnSubmitText="Taak aanmaken"
                            isValid={isValid}
                            isSubmitting={isSubmitting}
                            onHide={handleClose}
                            form="create-task"
                        />
                    </>
                )}
            </Formik>
        </FormModal>
    );

    async function handleSubmit(values, { setSubmitting }) {
        const documentUserIds = documentUsers.map((documentUser) => documentUser.userId);

        const formData = {
            targetType: 'block',
            targetId: `${block.id}`,
            targetIdType: 'int',
            purpose: values.purpose,
            description: values.description,
            deadline: values.deadline,
            reminderDate: values.reminderDate,
            directUserIds: values.userIds.filter((userId) => documentUserIds.includes(userId)),
            targetParent: {
                type: 'area',
                externalId: `${area.id}`,
                externalIdType: 'int',
                name: area.title,
                parent: {
                    type: 'section',
                    externalId: `${section.id}`,
                    externalIdType: 'int',
                    name: section.title,
                    parent: {
                        type: 'document',
                        externalId: `${document.id}`,
                        externalIdType: 'int',
                        name: document.name,
                    },
                },
            },
            content: await getContent(),
        };

        addApprovalItem(formData).then(() => {
            // Invalidate Tags
            dispatch(
                documentApi.util.invalidateTags([
                    { type: 'Block', id: `${block.id}` },
                    { type: 'Area', id: `${areaId}` },
                ]),
            );

            // Close modal
            setSubmitting(false);
            handleClose();
        });
    }

    async function getContent() {
        const documentVariants = await getDocumentVariants(area.documentId).unwrap();
        const baseVariant = documentVariants.find((variant) => variant.parentId === null);
        const areaData = await getAreaBlocks({ areaId: area.id, variantId: baseVariant.id }).unwrap();

        const contentBlock = areaData.areaBlocks.find((areaBlock) => areaBlock.id === block.id);

        let areaBlocks = [];

        if (contentBlock.type === 'blockLayout') {
            const blockIdsInLayout = DocumentHelper.getBlockIdsInLayout([contentBlock]);
            areaBlocks = areaData.areaBlocks.filter((areaBlock) => blockIdsInLayout.includes(areaBlock.id));
        }

        return {
            block: contentBlock,
            areaBlocks,
        };
    }
}

function ModalBody() {
    const { values } = useFormikContext();
    const teams = useGetApprovalTeams();
    const [getTeamMembers] = approvalApi.useLazyGetTeamMembersQuery();
    const users = useGetOrganisationUsers(true);
    const [teamOptions, setTeamOptions] = useState([]);

    const userIds = values.userIds;

    useEffect(() => {
        if (teams.length === 0 || users.length === 0) {
            return;
        }

        Promise.all(
            teams.map((team) => {
                return getTeamMembers(team.id)
                    .unwrap()
                    .then((teamMembers) => {
                        return {
                            ...team,
                            label: team.name,
                            value: team.id,
                            teamMembers: teamMembers
                                .filter((member) => userIds.includes(member.userId) === false)
                                .map((member) => {
                                    const user = users.find((user) => user.id === member.userId);

                                    return {
                                        label: user?.fullName ?? 'Onbekende gebruiker',
                                        value: member.userId,
                                    };
                                })
                                .sort(HelperFunctions.sortByString('label')),
                        };
                    });
            }),
        ).then((results) => {
            const teams = results
                .filter((team) => team.teamMembers.length > 0)
                .sort(HelperFunctions.sortByString('label'));

            setTeamOptions(teams);
        });
    }, [teams, users, userIds]);

    return (
        <>
            <div className="sidebar-form">
                <FormGroup label="Taak" htmlFor="purpose" help="" required={true}>
                    <InputSelect
                        name="purpose"
                        options={[
                            {
                                value: 'notify',
                                label: 'Kennisnemen',
                            },
                            {
                                value: 'approve',
                                label: 'Goedkeuren',
                            },
                            {
                                value: 'revise',
                                label: 'Herzien',
                            },
                            {
                                value: 'feedback',
                                label: 'Feedback geven',
                            },
                            {
                                value: 'check',
                                label: 'Controleren',
                            },
                            {
                                value: 'assess',
                                label: 'Toetsen',
                            },
                        ]}
                    />
                </FormGroup>

                <FormGroup label="Opmerkingen" htmlFor="description" help="" required={true}>
                    <TextAreaField
                        name="description"
                        props={{
                            rows: 3,
                            required: true,
                            placeholder: 'Opmerkingen',
                        }}
                    />
                </FormGroup>

                <FormGroup label="Deadline" htmlFor="deadline" help="">
                    <InputField
                        name="deadline"
                        props={{
                            id: 'deadline',
                            type: 'datetime-local',
                        }}
                    />
                </FormGroup>

                <FormGroup label="Reminder" htmlFor="reminderDate" help="">
                    <InputField
                        name="reminderDate"
                        props={{
                            id: 'reminderDate',
                            type: 'datetime-local',
                            disabled: values.deadline === '',
                        }}
                    />
                </FormGroup>

                <Teams teamOptions={teamOptions} />
                <DocumentUsers />
            </div>
        </>
    );
}

function Teams({ teamOptions }) {
    const { values, setFieldValue } = useFormikContext();

    return (
        <>
            <FormGroup label="Team toevoegen" htmlFor="add-team" help="Voeg alle gebruikers uit een team toe">
                <DocRevSelect
                    name="teams"
                    options={teamOptions}
                    onChange={handleChange}
                    props={{ placeholder: 'Selecteer een team...' }}
                />
            </FormGroup>
        </>
    );

    function handleChange(selectedValue) {
        setFieldValue('userIds', [
            ...values.userIds,
            ...selectedValue.teamMembers.map((teamMember) => teamMember.value),
        ]);
    }
}

function DocumentUsers() {
    const { values } = useFormikContext();
    const documentUsers = useGetDocumentUsers();
    const organisationUsers = useGetOrganisationUsers(true);

    return (
        <FieldArray name="userIds">
            {({ remove, push }) => (
                <>
                    <FormGroup label="Gebruiker toevoegen" htmlFor="add-user" help="Voeg een enkele gebruiker toe">
                        <AddUser push={push} />

                        <table className="table dr-table mt-4">
                            <tbody>
                                {values.userIds.length > 0 ? (
                                    <>
                                        {values.userIds.map((userId, index) => {
                                            const user = organisationUsers.find(
                                                (organisationUser) => organisationUser.id === userId,
                                            );

                                            return (
                                                <User
                                                    documentUser={documentUsers.find(
                                                        (documentUser) => documentUser.userId === userId,
                                                    )}
                                                    user={user}
                                                    index={index}
                                                    remove={remove}
                                                    key={`user-${userId}`}
                                                />
                                            );
                                        })}
                                    </>
                                ) : (
                                    <tr>
                                        <td colSpan={2}>
                                            <div className="text-muted">
                                                Er zijn nog geen gebruikers aan deze taak toegevoegd.
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </FormGroup>
                </>
            )}
        </FieldArray>
    );
}

/**
 * @param {import('models/documents.models').DocumentUser} [documentUser]
 * @param {import('models/auth.models').OrganisationUser} [user]
 * @param {number} index
 * @param {function(number): void} remove
 */
function User({ documentUser, user, index, remove }) {
    const documentUserRole = useGetDocumentUserRole(documentUser?.role);

    return (
        <tr>
            <td className="w-100">
                <div
                    className={cx('font-weight-bold', {
                        'text-muted': !documentUser,
                    })}
                    style={{ fontSize: 13, lineHeight: '21px' }}
                >
                    {user?.fullName}
                </div>
                {documentUser ? (
                    <>{documentUserRole}</>
                ) : (
                    <div className="flex-center text-muted">
                        <InfoCircle className="mr-1" />
                        Niet gekoppeld aan document
                    </div>
                )}
            </td>

            <td>
                <Button
                    variant="icon"
                    size="sm"
                    className="btn-icon-only text-danger ml-1"
                    onClick={() => remove(index)}
                >
                    <Trash3 size={16} />
                </Button>
            </td>
        </tr>
    );
}

/**
 * @param {function(string): void} push
 */
function AddUser({ push }) {
    const { values } = useFormikContext();
    const documentUsers = useGetDocumentUsers();
    const options = prepareData();

    return (
        <DocRevSelect
            name="documentUser"
            options={options}
            onChange={handleChange}
            props={{ placeholder: 'Selecteer een gebruiker...' }}
        />
    );

    function handleChange(selectedValue) {
        push(selectedValue.value);
    }

    function prepareData() {
        /** @type {string[]} */
        const userIds = values.userIds;

        return HelperFunctions.prepareDropdownData(
            documentUsers.filter((documentUser) => userIds.includes(documentUser.userId) === false),
            'fullName',
            'userId',
        );
    }
}

function FormLocalStorage({ formValues, setInitialValues }) {
    useEffect(() => {
        setInitialValues(JSON.parse(formValues));
    }, [formValues]);

    return null;
}

const TaskSchema = Yup.object().shape({
    description: Yup.string().required(),
    purpose: Yup.string().required(),
    userIds: Yup.array().min(1),
});
